import * as React from 'react';

import { Divider, Grid } from '@coursera/cds-core';

import { DEFAULT_ROLE_OPTIONS_LIMIT, ROLE_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useLearnerObjectUpdateInterestedCareers from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateInterestedCareers';
import { slugifyCustomRoleName } from 'bundles/onboarding-common/utils';
import AddCurrentRoleToDesiredRolesModal from 'bundles/personalization-settings/components/AddCurrentRoleToDesiredRolesModal';
import CurrentRoleSettingItem from 'bundles/personalization-settings/components/CurrentRoleSettingItem';
import DesiredRolesSettingItem from 'bundles/personalization-settings/components/DesiredRolesSettingItem';
import EducationLevelSettingItem from 'bundles/personalization-settings/components/EducationLevelSettingItem';
import LearningGoalSettingItem from 'bundles/personalization-settings/components/LearningGoalSettingItem';
import SettingItem from 'bundles/personalization-settings/components/SettingItem';
import SkillsSettingItem from 'bundles/personalization-settings/components/SkillsSettingItem';
import { type LearnerObjectFieldId, getLearnerObjectSettings } from 'bundles/personalization-settings/constants';
import { usePersonalizationSettingsContext } from 'bundles/personalization-settings/contexts/PersonalizationSettingsContext';
import useFindRoleInRolesCatalogByName from 'bundles/personalization-settings/hooks/useFindRoleInRolesCatalogByName';
import useResolvedCurrentRole from 'bundles/personalization-settings/hooks/useResolvedCurrentRole';
import useResolvedDesiredRoles from 'bundles/personalization-settings/hooks/useResolvedDesiredRoles';
import { SETTING_SAVE_SUCCESS_DISPLAY_MS } from 'bundles/personalization-settings/hooks/useSettingSave';
import { addRole } from 'bundles/personalization-settings/utils/desiredRolesV1';
import { shouldPromptAddCurrentRoleToDesiredRoles } from 'bundles/personalization-settings/utils/shouldPromptAddCurrentRoleToDesiredRoles';

// Every field reads its own saved value and loading state from
// PersonalizationSettingsContext -- each item component only owns its own
// mutation hook, plus any catalog/resolution queries it needs to turn that
// saved value into editor state.
const renderSettingItem = (id: LearnerObjectFieldId, label: string, onSelectImproveSkills: () => void) => {
  switch (id) {
    case 'learningGoal':
      return <LearningGoalSettingItem label={label} onSelectImproveSkills={onSelectImproveSkills} />;
    case 'skills':
      return <SkillsSettingItem label={label} />;
    case 'educationLevel':
      return <EducationLevelSettingItem label={label} />;
    case 'desiredRole':
      return <DesiredRolesSettingItem label={label} />;
    case 'currentRole':
      return <CurrentRoleSettingItem label={label} />;
    default:
      return <SettingItem label={label} />;
  }
};

/**
 * The list of explicit Learner Object settings rendered inside the
 * Personalization settings card — a separate responsibility from the tab shell.
 * Maps the single source-of-truth settings list to per-field rows.
 *
 * Also owns the add-current-role-to-desired-roles prompt: it resolves
 * `currentRole`/`desiredRole` the same way `CurrentRoleSettingItem` and
 * `DesiredRolesSettingItem` do (via the shared resolve hooks), so the check
 * runs against the same live, in-session values those rows display -- not
 * just the page-level query's initial seed. Confirming writes through the
 * same mutation `DesiredRolesSettingItem` uses and patches the shared
 * context, which `DesiredRolesSettingItem` picks up automatically (it has no
 * pending local edit of its own at that point).
 */
const LearnerObjectSettingItems = () => {
  const { values, loading, setFieldValue } = usePersonalizationSettingsContext();
  const { value: currentRole } = useResolvedCurrentRole(values.currentRole);
  const { value: desiredRoles } = useResolvedDesiredRoles(values.desiredRole);
  const { updateInterestedCareers } = useLearnerObjectUpdateInterestedCareers();
  const findRoleInCatalog = useFindRoleInRolesCatalogByName();
  const [promptOpen, setPromptOpen] = React.useState(false);
  const [addRoleLoading, setAddRoleLoading] = React.useState(false);
  const [addRoleNotificationType, setAddRoleNotificationType] = React.useState<'success' | 'error' | undefined>(
    undefined
  );
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  const handleSelectImproveSkills = () => {
    if (shouldPromptAddCurrentRoleToDesiredRoles(currentRole, desiredRoles, DEFAULT_ROLE_OPTIONS_LIMIT)) {
      clearTimeout(closeTimeoutRef.current);
      setAddRoleNotificationType(undefined);
      setPromptOpen(true);
    }
  };

  // Keeps the dialog open through the save and its outcome -- it's the only
  // surface the learner had open when they triggered this write, so its own
  // loading/notification state (not a page-level toast) is what they'll see.
  // On success the dialog auto-closes after the same display window the
  // other setting rows use, so the learner has time to read the confirmation.
  const handleConfirmAddCurrentRole = async () => {
    if (!currentRole) {
      setPromptOpen(false);
      return;
    }

    setAddRoleLoading(true);
    setAddRoleNotificationType(undefined);
    try {
      const catalogMatch = await findRoleInCatalog(currentRole.name);
      const nextRoles = addRole(
        desiredRoles,
        catalogMatch
          ? { type: 'DEFAULT', id: catalogMatch.id, name: catalogMatch.name }
          : { type: 'CUSTOM', name: currentRole.name }
      );

      const careers = nextRoles.map((role) =>
        role.type === 'DEFAULT'
          ? { type: 'DEFAULT' as const, id: role.id, name: role.name }
          : { type: 'CUSTOM' as const, id: slugifyCustomRoleName(role.name), name: role.name }
      );
      const result = await updateInterestedCareers(careers);
      if (result.success) {
        setFieldValue(
          'desiredRole',
          careers.map((career) => ({
            id: career.id,
            name: career.name,
            type: career.type === 'DEFAULT' ? ROLE_OPTIONS_TYPE.DEFAULT : ROLE_OPTIONS_TYPE.CUSTOM,
          }))
        );
        setAddRoleNotificationType('success');
        closeTimeoutRef.current = setTimeout(() => setPromptOpen(false), SETTING_SAVE_SUCCESS_DISPLAY_MS);
      } else {
        setAddRoleNotificationType('error');
      }
    } catch {
      setAddRoleNotificationType('error');
    } finally {
      setAddRoleLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Grid
        container
        direction="column"
        spacing={16}
        aria-busy={loading || undefined}
        data-testid={loading ? 'personalization-loading' : 'personalization-settings'}
      >
        {getLearnerObjectSettings().map(({ id, label }, index) => (
          <React.Fragment key={id}>
            {index > 0 && (
              <Grid item>
                <Divider />
              </Grid>
            )}
            <Grid item zeroMinWidth>
              {renderSettingItem(id, label, handleSelectImproveSkills)}
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
      <AddCurrentRoleToDesiredRolesModal
        open={promptOpen}
        roleName={currentRole?.name ?? ''}
        loading={addRoleLoading}
        notificationType={addRoleNotificationType}
        onConfirm={handleConfirmAddCurrentRole}
        onDismiss={() => setPromptOpen(false)}
      />
    </React.Fragment>
  );
};

export default LearnerObjectSettingItems;
